import { useEffect, useState } from "react";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg, fmtDate } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const PAGE = 50;
const sel = "h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm";

export function ActivityLog() {
  const [f, setF] = useState({ action: "", q: "", since: "", until: "" });
  const [skip, setSkip] = useState(0);
  const [d, setD] = useState({ items: [], total: 0, actions: [] });
  const [open, setOpen] = useState(null);
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  useEffect(() => {
    const t = setTimeout(() => api.get(`/admin/activity?${qs}&skip=${skip}&limit=${PAGE}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), 300);
    return () => clearTimeout(t);
  }, [qs, skip]);
  const set = (k) => (e) => { setSkip(0); setF({ ...f, [k]: e.target.value }); };
  return (
    <div className="space-y-3" data-testid="activity-log">
      <div className="panel p-4 flex flex-wrap gap-2 items-center">
        <select value={f.action} onChange={set("action")} className={sel} data-testid="activity-action-filter"><option value="">All actions</option>{d.actions.map((a) => <option key={a} value={a}>{a}</option>)}</select>
        <Input value={f.q} onChange={set("q")} placeholder="Search target (book, file, email…)" className="h-9 w-56" data-testid="activity-search" />
        <Input type="date" value={f.since} onChange={set("since")} className="h-9 w-40 [color-scheme:dark]" data-testid="activity-since" />
        <span className="text-xs text-slate-500">to</span>
        <Input type="date" value={f.until} onChange={set("until")} className="h-9 w-40 [color-scheme:dark]" data-testid="activity-until" />
        <a href={`${API}/admin/activity.csv?${qs}`} className="ml-auto inline-flex items-center gap-1 text-sm text-amber-200 hover:text-white" data-testid="activity-csv"><Download size={14} />CSV</a>
      </div>
      <div className="panel divide-y divide-slate-800/70">
        {!d.items.length && <p className="p-6 text-sm text-slate-500" data-testid="activity-empty">No activity matches these filters.</p>}
        {d.items.map((l) => (
          <button key={l.id} onClick={() => setOpen(open === l.id ? null : l.id)} className="w-full text-left px-4 py-2.5 hover:bg-slate-900/60 transition-colors" data-testid={`activity-row-${l.id}`}>
            <div className="flex flex-wrap justify-between gap-3 text-xs">
              <span><span className="font-mono text-amber-400/90 mr-2">{l.action}</span><span className="text-slate-300">{l.target}</span>{l.book_title && l.book_title !== l.target && <span className="text-slate-500"> · {l.book_title}</span>}</span>
              <span className="text-slate-500 shrink-0">{l.user_email} · {fmtDate(l.created_at)}</span>
            </div>
            {open === l.id && Object.keys(l.details || {}).length > 0 && <pre className="mt-2 text-[11px] text-slate-400 whitespace-pre-wrap break-all font-mono" data-testid="activity-details">{JSON.stringify(l.details, null, 2)}</pre>}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span data-testid="activity-total">{d.total ? `${skip + 1}–${Math.min(skip + PAGE, d.total)} of ${d.total}` : "0 entries"}</span>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="border-slate-700 h-8" disabled={!skip} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="activity-prev"><ChevronLeft size={14} /></Button>
          <Button size="sm" variant="outline" className="border-slate-700 h-8" disabled={skip + PAGE >= d.total} onClick={() => setSkip(skip + PAGE)} data-testid="activity-next"><ChevronRight size={14} /></Button>
        </div>
      </div>
    </div>
  );
}
